import { supabase } from '@/lib/supabase';

export type ContentTable =
  | 'media_posts'
  | 'businesses'
  | 'listings'
  | 'apartments'
  | 'jobs'
  | 'gigs'
  | 'courses'
  | 'forum_topics';

export interface ContentColumnDef {
  key: string;
  label: string;
  kind?: 'text' | 'money' | 'number' | 'status';
}

export interface EditFieldDef {
  key: string;
  label: string;
  kind?: 'text' | 'textarea' | 'number';
}

export interface ContentConfig {
  table: ContentTable;
  label: string;
  icon: string;
  titleKey: string;
  subtitleKey?: string;
  columns: ContentColumnDef[];
  editFields: EditFieldDef[];
}

export const CONTENT_CONFIGS: ContentConfig[] = [
  {
    table: 'media_posts',
    label: 'Médias',
    icon: 'ri-movie-line',
    titleKey: 'title',
    subtitleKey: 'artist_name',
    columns: [
      { key: 'title', label: 'Titre' },
      { key: 'category', label: 'Catégorie' },
      { key: 'artist_name', label: 'Artiste' },
      { key: 'views', label: 'Vues', kind: 'number' },
      { key: 'status', label: 'Statut', kind: 'status' },
    ],
    editFields: [
      { key: 'title', label: 'Titre' },
      { key: 'artist_name', label: 'Artiste' },
      { key: 'category', label: 'Catégorie' },
      { key: 'status', label: 'Statut' },
      { key: 'description', label: 'Description', kind: 'textarea' },
    ],
  },
  {
    table: 'businesses',
    label: 'Entreprises',
    icon: 'ri-building-2-line',
    titleKey: 'name',
    subtitleKey: 'city',
    columns: [
      { key: 'name', label: 'Nom' },
      { key: 'city', label: 'Ville' },
      { key: 'phone', label: 'Téléphone' },
      { key: 'status', label: 'Statut', kind: 'status' },
    ],
    editFields: [
      { key: 'name', label: 'Nom' },
      { key: 'city', label: 'Ville' },
      { key: 'phone', label: 'Téléphone' },
      { key: 'email', label: 'Email' },
      { key: 'status', label: 'Statut' },
      { key: 'description', label: 'Description', kind: 'textarea' },
    ],
  },
  {
    table: 'listings',
    label: 'Annonces',
    icon: 'ri-price-tag-3-line',
    titleKey: 'title',
    subtitleKey: 'city',
    columns: [
      { key: 'title', label: 'Titre' },
      { key: 'type', label: 'Type' },
      { key: 'city', label: 'Ville' },
      { key: 'price', label: 'Prix', kind: 'money' },
      { key: 'status', label: 'Statut', kind: 'status' },
    ],
    editFields: [
      { key: 'title', label: 'Titre' },
      { key: 'type', label: 'Type' },
      { key: 'city', label: 'Ville' },
      { key: 'status', label: 'Statut' },
      { key: 'description', label: 'Description', kind: 'textarea' },
    ],
  },
  {
    table: 'apartments',
    label: 'Immobilier',
    icon: 'ri-home-4-line',
    titleKey: 'title',
    subtitleKey: 'quartier',
    columns: [
      { key: 'title', label: 'Bien' },
      { key: 'quartier', label: 'Quartier' },
      { key: 'price', label: 'Prix', kind: 'money' },
      { key: 'status', label: 'Statut', kind: 'status' },
    ],
    editFields: [
      { key: 'title', label: 'Titre' },
      { key: 'quartier', label: 'Quartier' },
      { key: 'city', label: 'Ville' },
      { key: 'status', label: 'Statut' },
      { key: 'description', label: 'Description', kind: 'textarea' },
    ],
  },
  {
    table: 'jobs',
    label: 'Emplois',
    icon: 'ri-briefcase-line',
    titleKey: 'title',
    subtitleKey: 'company',
    columns: [
      { key: 'title', label: 'Poste' },
      { key: 'company', label: 'Entreprise' },
      { key: 'city', label: 'Ville' },
      { key: 'contract_type', label: 'Contrat' },
      { key: 'status', label: 'Statut', kind: 'status' },
    ],
    editFields: [
      { key: 'title', label: 'Poste' },
      { key: 'company', label: 'Entreprise' },
      { key: 'city', label: 'Ville' },
      { key: 'contract_type', label: 'Contrat' },
      { key: 'status', label: 'Statut' },
      { key: 'description', label: 'Description', kind: 'textarea' },
    ],
  },
  {
    table: 'gigs',
    label: 'Services',
    icon: 'ri-tools-line',
    titleKey: 'title',
    subtitleKey: 'freelancer',
    columns: [
      { key: 'title', label: 'Service' },
      { key: 'freelancer', label: 'Freelance' },
      { key: 'price', label: 'Prix', kind: 'money' },
      { key: 'status', label: 'Statut', kind: 'status' },
    ],
    editFields: [
      { key: 'title', label: 'Service' },
      { key: 'freelancer', label: 'Freelance' },
      { key: 'category', label: 'Catégorie' },
      { key: 'status', label: 'Statut' },
      { key: 'description', label: 'Description', kind: 'textarea' },
    ],
  },
  {
    table: 'courses',
    label: 'Cours',
    icon: 'ri-graduation-cap-line',
    titleKey: 'title',
    subtitleKey: 'instructor',
    columns: [
      { key: 'title', label: 'Cours' },
      { key: 'instructor', label: 'Formateur' },
      { key: 'price', label: 'Prix', kind: 'money' },
      { key: 'status', label: 'Statut', kind: 'status' },
    ],
    editFields: [
      { key: 'title', label: 'Cours' },
      { key: 'instructor', label: 'Formateur' },
      { key: 'category', label: 'Catégorie' },
      { key: 'status', label: 'Statut' },
      { key: 'description', label: 'Description', kind: 'textarea' },
    ],
  },
  {
    table: 'forum_topics',
    label: 'Forum',
    icon: 'ri-chat-3-line',
    titleKey: 'title',
    subtitleKey: 'category',
    columns: [
      { key: 'title', label: 'Sujet' },
      { key: 'category', label: 'Catégorie' },
      { key: 'views', label: 'Vues', kind: 'number' },
      { key: 'status', label: 'Statut', kind: 'status' },
    ],
    editFields: [
      { key: 'title', label: 'Sujet' },
      { key: 'category', label: 'Catégorie' },
      { key: 'status', label: 'Statut' },
      { key: 'body', label: 'Contenu', kind: 'textarea' },
    ],
  },
];

export type ContentRow = Record<string, unknown>;

export async function fetchContentRows(table: ContentTable): Promise<ContentRow[]> {
  const { data, error } = await supabase
    .from(table)
    .select('*')
    .order('created_at', { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data as ContentRow[]) || [];
}

export async function deleteContentRow(table: ContentTable, id: string): Promise<void> {
  const { error } = await supabase.from(table).delete().eq('id', id);
  if (error) throw error;
}

export async function updateContentRow(
  table: ContentTable,
  id: string,
  fields: Record<string, unknown>,
): Promise<void> {
  const { error } = await supabase.from(table).update(fields).eq('id', id);
  if (error) throw error;
}